import type { Rail } from "../level";
import { coping, type QuarterPrim } from "./quarter";
import { rad, type PieceKind } from "./types";

export interface HalfpipeSpec {
  /** Centre of the flat bottom. */
  x: number;
  z: number;
  /** Direction from one wall to the other: the walls rise toward ±(cos rot, sin rot). */
  rot: number;
  /** Transition radius (and wall height). */
  r: number;
  /** Half the length of the walls, along the coping. */
  hw: number;
  /** Length of the flat bottom between the two transitions. */
  flat: number;
  deck: number;
}

/** Two quarters facing across a flat bottom (see `halfpipe`). */
export interface HalfpipePiece {
  kind: "halfpipe";
  x: number;
  z: number;
  rot?: number;
  r: number;
  hw: number;
  flat: number;
  deck: number;
}

/**
 * Two quarter pipes facing each other across a flat bottom, with coping on
 * both. The quarters are ordinary `quarter` primitives, so vert, landings and
 * copings behave exactly as on a lone quarter pipe.
 */
export function halfpipe(spec: HalfpipeSpec): { prims: QuarterPrim[]; rails: Rail[] } {
  const c = Math.cos(spec.rot);
  const s = Math.sin(spec.rot);
  const off = spec.flat / 2;
  const wall = (sign: 1 | -1): QuarterPrim => ({
    kind: "quarter",
    x: spec.x + sign * off * c,
    z: spec.z + sign * off * s,
    rot: sign === 1 ? spec.rot : spec.rot + Math.PI,
    r: spec.r,
    hw: spec.hw,
    deck: spec.deck,
  });
  const walls = [wall(1), wall(-1)];
  return { prims: walls, rails: walls.map(coping) };
}

export const HALFPIPE_PIECE: PieceKind<HalfpipePiece> = {
  fields: ["x", "z", "rot", "r", "hw", "flat", "deck"],
  placed: true,
  check(c, o, path, bounds) {
    c.num(o, "r", path, { min: 0.5, max: 20 });
    c.num(o, "flat", path, { min: 0, max: 2 * bounds });
    c.num(o, "deck", path, { min: 0, max: 2 * bounds });
  },
  compile(p, out) {
    const hp = halfpipe({ x: p.x, z: p.z, rot: rad(p.rot), r: p.r, hw: p.hw, flat: p.flat, deck: p.deck });
    out.prims.push(...hp.prims);
    out.rails.push(...hp.rails);
  },
};
